import React, { useCallback, useEffect, useState } from 'react';
import { Flex, Item, View, ProgressCircle, ListBox, Link } from '@adobe/react-spectrum';
import { statusColor, truncateText } from '../../../../../utils/helperUtil';
import { ChevronDown, ChevronUp, CircleFill } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../state/store';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { ListRegisterRequest } from '../../../../../services/soap/features/requests/ListRegisterRequest';
import { ListStatusRequest, ListStatusType } from '../../../../../services/soap/project/requests/ListStatusRequest';
import { useTranslation } from 'react-i18next';
import styles from './project_status_filter_component.module.css';

interface ProjectStatusFilterComponentProps {
  createParams: (filterType: any, filterValue: any) => void;
  filterParams?: any;
}

const ProjectStatusFilterComponent: React.FC<ProjectStatusFilterComponentProps> = ({ createParams, filterParams }) => {
  const { t } = useTranslation();
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { store, projectService, featureService } = useDependency();
  const [toggleListVisibility, setToggleListVisibility] = useState<any>({ projectStatusList: true });
  const [projectStatusSelectedKey, setProjectStatusSelectedKey] = useState<Set<string>>(new Set(['All states']));
  const [projectStatusList, setProjectStatusList] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      setProjectStatusSelectedKey(new Set(['All states']));
      await getProjectStatus();
    })();
  }, [selectedApp]);

  const getProjectStatus = useCallback(async () => {
    try {
      let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
      //console.log('resListRegister', resListRegister);
      let projectActivityValue = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListProject')?.ROW.value ?? '';

      //console.log('projectActivityValue', projectActivityValue);

      let resProjectStatus: any = await projectService.listProjectStatus(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Project, projectActivityValue));
      //console.log('resProjectStatus', resProjectStatus);
      if (resProjectStatus?.result === 'OK') {
        let projectStatusArray: any = [];
        resProjectStatus?.STATUS_LIST.forEach((status: any) => {
          const { id, name, color } = status.STATUS;
          let colorString = statusColor(color);
          // Push an object containing id and name to the statusArray
          projectStatusArray.push({ id, name, color: colorString });
        });
        //projectStatusArray.unshift({ id: 'All states', name: 'All states' });
        //console.log('projectStatusArray', projectStatusArray);
        setProjectStatusList(projectStatusArray);
      }
    } finally {
      setShowLoader(false);
    }
  }, [selectedApp]);

  const handleSelectionChange = (value: any) => {
    const arrValue = Array.from(value);
    const allProjectTypeCheck = arrValue.includes('All states') && arrValue.indexOf('All states') !== 0;

    if (allProjectTypeCheck) {
      const allProjectTypeSet = new Set(['All states']);
      setProjectStatusSelectedKey(allProjectTypeSet);
      createParams('customState', allProjectTypeSet);
    } else {
      const filteredValue = arrValue.filter(item => item !== 'All states');
      const updateValue = filteredValue.length > 0 ? new Set(filteredValue) : new Set(['All states']);
      setProjectStatusSelectedKey(new Set(Array.from(updateValue).map(String)));
      createParams('customState', updateValue);
    }
  };

  if (showLoader) {
    return (
      <Flex width="100%" height="100%" UNSAFE_style={{ backgroundColor: 'white' }} justifyContent={'center'} alignContent={'center'} alignItems={'center'}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <Flex direction={'column'} minWidth={'200px'} maxHeight={'300px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_style={{ overflowY: 'auto' }}>
        <Flex direction={'column'} gap={'4px'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.project_status_container}>
          <Flex direction={'row'} height={'36px'} alignSelf={'stretch'} alignItems={'center'} justifyContent={'space-between'} UNSAFE_className={styles.project_status_heading}>
            <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
              <View position={'relative'} UNSAFE_className={styles.project_status_heading_text}>
                {t('project_status', { ns: 'layout_components' })}
              </View>
            </Flex>
            {toggleListVisibility.projectStatusList ? (
              <ChevronDown color="#B0BEC5" onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, projectStatusList: false }))} style={{ cursor: 'pointer' }} />
            ) : (
              <ChevronUp onClick={() => setToggleListVisibility((preValue: any) => ({ ...preValue, projectStatusList: true }))} style={{ cursor: 'pointer' }} />
            )}
          </Flex>
          <Flex
            direction={'column'}
            alignSelf={'stretch'}
            alignItems={'start'}
            justifyContent={'start'}
            UNSAFE_className={styles.project_status_component}
            UNSAFE_style={{ display: toggleListVisibility.projectStatusList ? '' : 'none' }}
          >
            <ListBox
              id="listProjectStatus"
              width="100%"
              selectionMode="multiple"
              defaultSelectedKeys={projectStatusSelectedKey}
              selectedKeys={projectStatusSelectedKey}
              items={projectStatusList}
              onSelectionChange={(value: any) => {
                handleSelectionChange(value);
              }}
              UNSAFE_style={{ color: '#455a64 !important' }}
            >
              {(item: any) => (
                <Item key={item.id}>
                  <Flex direction={'row'} height={'30px'} UNSAFE_style={{ paddingBottom: '30px' }}>
                    <View UNSAFE_style={{ paddingTop: '4px', paddingLeft: '4px' }}>
                      <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.color} />
                    </View>
                    <View UNSAFE_style={{ whiteSpace: 'nowrap', color: '#455a64 !important', paddingTop: '5px' }}>
                      <span title={item.name}>{truncateText(item.name, 18)}</span>
                    </View>
                  </Flex>
                </Item>
              )}
            </ListBox>
            <View position={'relative'} width={'100%'} height={'20px'} UNSAFE_className={styles.project_status_clear_all_container}>
              <View position={'relative'} UNSAFE_className={styles.project_status_clear_all_text}>
                <Link
                  onPress={() => {
                    handleSelectionChange(new Set(['All states']));
                  }}
                >
                  {t('clear_all', { ns: 'layout_components' })}
                </Link>
              </View>
            </View>
          </Flex>
        </Flex>
      </Flex>
    );
  }
};

export default ProjectStatusFilterComponent;
